import { ensureValidAccessToken } from '@services/api-client'
import { userStorage } from '@services/utils/local-storage'

const API_BASE_URL =
  import.meta.env.VITE_LIVABILITY_API_URL || 'http://localhost:8000'

export function getLivabilityBaseUrl(): string {
  return API_BASE_URL.replace(/\/$/, '')
}

export function getLivabilityAuthHeaders(init?: RequestInit): Headers {
  const headers = new Headers(init?.headers)
  const bearer = userStorage.getBearer()
  if (bearer) {
    headers.set('Authorization', bearer)
  }
  return headers
}

export function normalizeNoneValues<T>(obj: unknown): T {
  if (Array.isArray(obj)) {
    return obj.map((item) => normalizeNoneValues(item)) as T
  }

  if (obj !== null && typeof obj === 'object') {
    const normalized: Record<string, unknown> = {}
    for (const key in obj as Record<string, unknown>) {
      const value = (obj as Record<string, unknown>)[key]
      normalized[key] =
        value === 'None' ? null : normalizeNoneValues(value)
    }
    return normalized as T
  }

  return (obj === 'None' ? null : obj) as T
}

export async function livFetchResponse(
  path: string,
  init?: RequestInit
): Promise<Response> {
  const url = `${getLivabilityBaseUrl()}${path.startsWith('/') ? path : `/${path}`}`

  await ensureValidAccessToken()

  let response = await fetch(url, {
    ...init,
    headers: getLivabilityAuthHeaders(init)
  })

  if (response.status === 401 && userStorage.get()) {
    if (await ensureValidAccessToken(true)) {
      response = await fetch(url, {
        ...init,
        headers: getLivabilityAuthHeaders(init)
      })
    }
  }

  return response
}

export async function livFetch<T>(
  path: string,
  init?: RequestInit
): Promise<T> {
  const response = await livFetchResponse(path, init)

  if (!response.ok) {
    throw new Error(`Livability API error: ${response.status} ${response.statusText}`)
  }

  const data = await response.json()
  return normalizeNoneValues<T>(data)
}

export async function livFetchVoid(
  path: string,
  init?: RequestInit
): Promise<void> {
  const response = await livFetchResponse(path, init)

  if (!response.ok) {
    throw new Error(`Livability API error: ${response.status} ${response.statusText}`)
  }
}
